import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, Companion } from '../store/gameStore';
import { useOptimizedCalculations } from '../store/optimizedGameStore';
import CompanionSlotMachineModal from './CompanionSlotMachineModal';
import CompanionUpgrade from './CompanionUpgrade';
import CompanionUpgradeModal from './CompanionUpgradeModal';
import CompanionTrainingModal from './CompanionTrainingModal';
import CompanionSacrifice from './CompanionSacrifice';
import CompanionSacrificeModal from './CompanionSacrificeModal';
import CompanionCardSelection from './CompanionCardSelection';
import CompanionCardSelectionModal from './CompanionCardSelectionModal';
import CompanionArenaModal from './CompanionArenaModal';
import CompanionConquest from './CompanionConquest';
import CompanionConquestModal from './CompanionConquestModal';
import ActiveCompanionsModal from './ActiveCompanionsModal';
import CompanionCollectionModal from './CompanionCollectionModal';
import CompanionResearchModal from './CompanionResearchModal';
import CompanionForgeModal from './CompanionForgeModal';
import CompanionInventoryModal from './CompanionInventoryModal';
import CompanionArcadeModal from './CompanionArcadeModal';
import CompanionStockMarketModal from './CompanionStockMarketModal';



interface CompanionManagerProps {
  showFreePremiumSpin?: boolean;
  onFreePremiumSpinComplete?: () => void;
}

const CompanionManager: React.FC<CompanionManagerProps> = ({ 
  showFreePremiumSpin = false, 
  onFreePremiumSpinComplete 
}) => {
  const { companions, getMaxCompanionSlots, playerLevel, formatNumber, getCompanionBonus } = useGameStore();
  const { setActiveCompanionOptimized, removeActiveCompanionOptimized } = useOptimizedCalculations();
  
  // Zustand für aufklappbare Bereiche
  const [expandedSections, setExpandedSections] = useState({
    training: false,
    arena: false,
    conquest: false,
    activeCompanions: false,
    collection: false
  });

  // State für Modals
  const [showSlotMachineModal, setShowSlotMachineModal] = useState(false);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showArenaModal, setShowArenaModal] = useState(false);
  const [showConquestModal, setShowConquestModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSacrificeModal, setShowSacrificeModal] = useState(false);
  const [showCardSelectionModal, setShowCardSelectionModal] = useState(false);
  const [showActiveCompanionsModal, setShowActiveCompanionsModal] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [showResearchModal, setShowResearchModal] = useState(false);
  const [showForgeModal, setShowForgeModal] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showArcadeModal, setShowArcadeModal] = useState(false);
  const [showStockMarketModal, setShowStockMarketModal] = useState(false);

  // Filter-Zustand für Begleiter-Sammlung
  const [filters, setFilters] = useState({
    rarity: 'all' as 'all' | 'common' | 'rare' | 'epic' | 'legendary' | 'godly' | 'mystic',
    ability: 'all' as 'all' | 'clickMultiplier' | 'coinPerSecond' | 'gemChance' | 'xpBonus' | 'gardenBonus' | 'autoClickPower' | 'upgradeDiscount' | 'prestigeBonus' | 'criticalHit' | 'healing' | 'shield' | 'counterAttack' | 'lifeSteal' | 'armorPenetration',
    status: 'all' as 'all' | 'active' | 'inactive',
    search: ''
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getRarityColor = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'from-yellow-400 to-orange-500';
      case 'epic': return 'from-purple-400 to-pink-500';
      case 'rare': return 'from-blue-400 to-cyan-500';
      case 'common': return 'from-gray-400 to-gray-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getRarityBorder = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'border-yellow-400';
      case 'epic': return 'border-purple-400';
      case 'rare': return 'border-blue-400';
      case 'common': return 'border-gray-400';
      default: return 'border-gray-400';
    }
  };

  const getRarityName = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'Legendär';
      case 'epic': return 'Episch';
      case 'rare': return 'Selten';
      case 'common': return 'Gewöhnlich';
      default: return 'Unbekannt';
    }
  };

  const isCompanionActive = (companion: Companion) => {
    return companions.active.some(activeCompanion => activeCompanion?.id === companion.id);
  };

  const isGodlyLegendary = (companion: Companion) => {
    return companion.name.startsWith('★') && companion.name.endsWith('★');
  };

  const isMysticDivine = (companion: Companion) => {
    return companion.name.startsWith('★★★') && companion.name.endsWith('★★★');
  };

  const getCompanionSlot = (companion: Companion) => {
    return companions.active.findIndex(activeCompanion => activeCompanion?.id === companion.id);
  };

  const handleCompanionClick = (companion: Companion) => {
    if (isCompanionActive(companion)) {
      // Entferne den Begleiter
      const slot = getCompanionSlot(companion);
      if (slot !== -1) {
        removeActiveCompanionOptimized(slot);
      }
    } else {
      // Füge den Begleiter hinzu
      setActiveCompanionOptimized(companion);
    }
  };

  const handleSlotClick = (slotIndex: number, companion?: Companion) => {
    if (companion) {
      // Entferne Begleiter aus dem Slot
      removeActiveCompanionOptimized(slotIndex);
    }
  };

  // Filter-Funktionen
  const getFilteredCompanions = () => {
    return companions.discovered.filter(companion => {
      // Rarity Filter
      if (filters.rarity !== 'all') {
        if (filters.rarity === 'mystic' && !isMysticDivine(companion)) return false;
        if (filters.rarity === 'godly' && !isGodlyLegendary(companion)) return false;
        if (filters.rarity !== 'mystic' && filters.rarity !== 'godly' && companion.type !== filters.rarity) return false;
        if (filters.rarity !== 'mystic' && filters.rarity !== 'godly' && (isGodlyLegendary(companion) || isMysticDivine(companion))) return false;
      }

      // Ability Filter
      if (filters.ability !== 'all') {
        const hasAbility = companion.abilities.some(ability => ability.type === filters.ability);
        if (!hasAbility) return false;
      }

      // Status Filter
      if (filters.status !== 'all') {
        const isActive = isCompanionActive(companion);
        if (filters.status === 'active' && !isActive) return false;
        if (filters.status === 'inactive' && isActive) return false;
      }

      // Search Filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const nameMatch = companion.name.toLowerCase().includes(searchLower);
        const abilityMatch = companion.abilities.some(ability => 
          ability.description.toLowerCase().includes(searchLower) ||
          ability.type.toLowerCase().includes(searchLower)
        );
        if (!nameMatch && !abilityMatch) return false;
      }

      return true;
    });
  };

  const resetFilters = () => {
    setFilters({
      rarity: 'all',
      ability: 'all', 
      status: 'all',
      search: ''
    });
  };

  if (companions.discovered.length === 0) {
    return (
      <div className="space-y-6">
        {/* Slot Machine - immer verfügbar */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-600">
          <motion.button
            onClick={() => setShowSlotMachineModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-gray-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(55, 65, 81, 0.5)' }}
          >
            <span className="text-3xl mb-2">🎰</span>
            <span className="text-sm font-bold text-center">Glücksautomat</span>
            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full mt-1">VERFÜGBAR</span>
          </motion.button>
        </div>

        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 text-center border border-gray-600">
          <div className="text-4xl mb-4">🔍</div>
          <h3 className="text-xl font-bold text-white mb-2">Keine Begleiter entdeckt</h3>
          <p className="text-gray-300">
            Nutze die Slot-Machine, um mächtige Begleiter zu erhalten!
          </p>
        </div>

        {/* Modals */}
        <CompanionSlotMachineModal
          isOpen={showSlotMachineModal}
          onClose={() => setShowSlotMachineModal(false)}
          isFreePremiumSpin={showFreePremiumSpin}
          onFreePremiumSpinComplete={onFreePremiumSpinComplete}
        />
      </div>
    );
  }

      return (
      <div className="grid grid-cols-3 gap-4 mt-6">
        {/* Begleiter-Sammlung */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-600">
          <motion.button
            onClick={() => setShowCollectionModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-gray-700/50 active:bg-gray-700/70 rounded-xl transition-colors touch-manipulation h-32"
            whileHover={{ backgroundColor: 'rgba(55, 65, 81, 0.5)' }}
          >
            <span className="text-3xl mb-2">📚</span>
            <span className="text-sm font-bold text-center">Sammlung ({companions.discovered.length})</span>
          </motion.button>
        </div>

        {/* Aktive Begleiter */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-600">
          <motion.button
            onClick={() => setShowActiveCompanionsModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-gray-700/50 active:bg-gray-700/70 rounded-xl transition-colors touch-manipulation h-32"
            whileHover={{ backgroundColor: 'rgba(55, 65, 81, 0.5)' }}
          >
            <span className="text-3xl mb-2">⭐</span>
            <span className="text-sm font-bold text-center">Aktive ({companions.active.filter(c => c).length}/{getMaxCompanionSlots()})</span>
          </motion.button>
        </div>
        
        {/* Begleiter-Upgrade-System */}
        <div className="bg-gradient-to-br from-purple-800 to-blue-800 rounded-xl border border-purple-600">
          <motion.button
            onClick={() => setShowUpgradeModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-purple-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(147, 51, 234, 0.5)' }}
          >
            <span className="text-3xl mb-2">⚡</span>
            <span className="text-sm font-bold text-center">Upgrade</span>
          </motion.button>
        </div>
        
        {/* Begleiter-Training & Reisen */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-600">
          <motion.button
            onClick={() => setShowTrainingModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-gray-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(55, 65, 81, 0.5)' }}
          >
            <span className="text-3xl mb-2">🏃‍♂️</span>
            <span className="text-sm font-bold text-center">Training</span>
          </motion.button>
        </div>
        
        {/* Arena-Kampf-System */}
        <div className="bg-gradient-to-br from-red-800 to-orange-800 rounded-xl border border-red-600">
          <motion.button
            onClick={() => setShowArenaModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-red-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(185, 28, 28, 0.5)' }}
          >
            <span className="text-3xl mb-2">⚔️</span>
            <span className="text-sm font-bold text-center">Arena</span>
            <span className="text-xs bg-red-600 text-white px-2 py-1 rounded-full mt-1">5v5</span>
          </motion.button>
        </div>
        
        {/* Eroberungs-System */}
        <div className="bg-gradient-to-br from-yellow-800 to-green-800 rounded-xl border border-yellow-600">
          <motion.button
            onClick={() => setShowConquestModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-yellow-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(202, 138, 4, 0.5)' }}
          >
            <span className="text-3xl mb-2">🗺️</span>
            <span className="text-sm font-bold text-center">Eroberung</span>
          </motion.button>
        </div>
        

        
        {/* Göttliches Opfer-System */}
        <div className="bg-gradient-to-br from-red-900 to-orange-900 rounded-xl border border-red-600">
          <motion.button
            onClick={() => setShowSacrificeModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-red-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(185, 28, 28, 0.5)' }}
          >
            <span className="text-3xl mb-2">⚡</span>
            <span className="text-sm font-bold text-center">Opfer</span>
            <span className="text-xs bg-red-600 text-white px-2 py-1 rounded-full mt-1">PERMANENT</span>
          </motion.button>
        </div>
        
        {/* Forschungs-System */}
        <div className="bg-gradient-to-br from-purple-800 to-indigo-800 rounded-xl border border-purple-600">
          <motion.button
            onClick={() => setShowResearchModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-purple-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(147, 51, 234, 0.5)' }}
          >
            <span className="text-3xl mb-2">🧬</span>
            <span className="text-sm font-bold text-center">Forschung</span>
          </motion.button>
        </div>
        
        {/* Schmiede-System */}
        <div className="bg-gradient-to-br from-orange-800 to-red-800 rounded-xl border border-orange-600">
          <motion.button
            onClick={() => setShowForgeModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-orange-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(251, 146, 60, 0.5)' }}
          >
            <span className="text-3xl mb-2">🔨</span>
            <span className="text-sm font-bold text-center">Schmiede</span>
          </motion.button>
        </div>
        
        {/* Arcade-System */}
        <div className="bg-gradient-to-br from-purple-800 to-blue-900 rounded-xl border border-purple-600">
          <motion.button
            onClick={() => setShowArcadeModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-purple-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(126, 34, 206, 0.5)' }}
          >
            <span className="text-3xl mb-2">🎮</span>
            <span className="text-sm font-bold text-center">Arcade</span>
          </motion.button>
        </div>

        {/* Börse-System */}
        <div className="bg-gradient-to-br from-blue-800 to-indigo-800 rounded-xl border border-blue-600">
          <motion.button
            onClick={() => setShowStockMarketModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-blue-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(59, 130, 246, 0.5)' }}
          >
            <span className="text-3xl mb-2">📈</span>
            <span className="text-sm font-bold text-center">Börse</span>
          </motion.button>
        </div>

        {/* Inventar-System */}
        <div className="bg-gradient-to-br from-green-800 to-blue-800 rounded-xl border border-green-600">
          <motion.button
            onClick={() => setShowInventoryModal(true)}
            className="w-full p-4 flex flex-col items-center justify-center text-white hover:bg-green-700/50 rounded-xl transition-colors h-32"
            whileHover={{ backgroundColor: 'rgba(34, 197, 94, 0.5)' }}
          >
            <span className="text-3xl mb-2">📦</span>
            <span className="text-sm font-bold text-center">Inventar</span>
          </motion.button>
        </div>

      {/* Modals */}
      <CompanionSlotMachineModal
        isOpen={showSlotMachineModal}
        onClose={() => setShowSlotMachineModal(false)}
        isFreePremiumSpin={showFreePremiumSpin}
        onFreePremiumSpinComplete={onFreePremiumSpinComplete}
      />
      
      <CompanionTrainingModal
        isOpen={showTrainingModal}
        onClose={() => setShowTrainingModal(false)}
      />
      
      <CompanionArenaModal
        isOpen={showArenaModal}
        onClose={() => setShowArenaModal(false)}
      />

      <CompanionConquestModal
        isOpen={showConquestModal}
        onClose={() => setShowConquestModal(false)}
      />

      <CompanionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />

      <CompanionSacrificeModal
        isOpen={showSacrificeModal}
        onClose={() => setShowSacrificeModal(false)}
      />

      <CompanionCardSelectionModal
        isOpen={showCardSelectionModal}
        onClose={() => setShowCardSelectionModal(false)}
      />

            <ActiveCompanionsModal
        isOpen={showActiveCompanionsModal}
        onClose={() => setShowActiveCompanionsModal(false)}
      />

      <CompanionCollectionModal
        isOpen={showCollectionModal}
        onClose={() => setShowCollectionModal(false)}
      />

      <CompanionResearchModal
        isOpen={showResearchModal}
        onClose={() => setShowResearchModal(false)}
      />

      <CompanionForgeModal
        isOpen={showForgeModal}
        onClose={() => setShowForgeModal(false)}
      />

      <CompanionInventoryModal
        isOpen={showInventoryModal}
        onClose={() => setShowInventoryModal(false)}
      />

      <CompanionArcadeModal
        isOpen={showArcadeModal}
        onClose={() => setShowArcadeModal(false)}
        onCardSelection={() => setShowCardSelectionModal(true)}
        onSlotMachine={() => setShowSlotMachineModal(true)}
      />

      <CompanionStockMarketModal
        isOpen={showStockMarketModal}
        onClose={() => setShowStockMarketModal(false)}
      />
    </div>
  );
};

export default CompanionManager; 